// =============================================
// /src/pages/AvatarFrameGroupList.tsx
// 一覧ページ：publicOpen / requiredGrants を追加表示
// =============================================
import React, { useEffect, useState } from 'react';
import GenericTemplate from '../components/templates/GenericTemplate';
import { useHistory } from 'react-router-dom';
import { listAvatarFrameGroups } from '../common/firebase/avatarFrames';
import type { AvatarFrameGroup } from '../types/avatarFrame';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const Thumb: React.FC<{ src?: string; alt?: string }> = ({ src, alt }) => (
  <Box
    sx={{
      width: 56,
      height: 56,
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: '#f5f5f5',
    }}
  >
    {src ? (
      <img
        src={src}
        alt={alt}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : null}
  </Box>
);

// requiredGrants をチップで整形（最大3つ + 残数）
const GrantsChips: React.FC<{ grants?: string[] }> = ({ grants }) => {
  const g = grants ?? [];
  if (g.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  const head = g.slice(0, 3);
  const rest = g.length - head.length;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {head.map((s) => (
        <Chip key={s} size="small" label={s} />
      ))}
      {rest > 0 && <Chip size="small" label={`+${rest}`} variant="outlined" />}
    </Stack>
  );
};

const AvatarFrameGroupList: React.FC = () => {
  const history = useHistory();
  const [rows, setRows] = useState<AvatarFrameGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAvatarFrameGroups();
      setRows(data);
    } catch (e: any) {
      setError(e?.message ?? '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  return (
    <GenericTemplate title="アバターフレームグループ一覧">
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          {/* <Typography variant="h5">アバターフレームグループ一覧</Typography> */}
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => history.push('/AvatarFrameGroupNew')}
          >
            新規作成
          </Button>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>グループID</TableCell>
                  <TableCell>表示名</TableCell>
                  <TableCell>フレーム画像</TableCell>
                  <TableCell>有効</TableCell>
                  <TableCell>公開</TableCell> {/* ← publicOpen */}
                  <TableCell>条件（requiredGrants）</TableCell> {/* ← grants */}
                  <TableCell>順序</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((g) => {
                  const firstUri =
                    g.frames?.find((f) => f.enabled && f.uri)?.uri ||
                    g.frames?.[0]?.uri;
                  const isPublic = !!g.publicOpen; // undefined → false 扱い
                  return (
                    <TableRow key={g.id} hover>
                      <TableCell>{g.id}</TableCell>
                      <TableCell>{g.name}</TableCell>
                      <TableCell>
                        <Thumb src={firstUri} alt={g.name} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={g.enabled ? 'ON' : 'OFF'}
                          color={g.enabled ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isPublic ? '全員公開' : '条件付き'}
                          color={
                            isPublic
                              ? 'success'
                              : g.requiredGrants?.length
                              ? 'warning'
                              : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {isPublic ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <GrantsChips grants={g.requiredGrants} />
                        )}
                      </TableCell>
                      <TableCell>{g.order}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              history.push(`AvatarFrameGroupEdit/${g.id}`)
                            }
                          >
                            編集
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </GenericTemplate>
  );
};

export default AvatarFrameGroupList;
